import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Tooltip } from '@mui/material';
import { Autocomplete, TextField, Button, IconButton } from '@totalsoft/rocket-ui';
import { Table, TableCell, TableBody, TableRow, TableHead } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MemoryIcon from '@mui/icons-material/Memory';
import MultilineEditor from '../../../components/common/inputs/MultilineEditor';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import XMLViewer from 'react-xml-viewer';
import { CopyToClipboard } from 'react-copy-to-clipboard';

const GenericEvaluationComponent = ({
  evaluatingExpression,
  evaluationParameters,
  evaluationResult,
  genericEvaluationEntities,
  onGenericEvaluationParameterChanged,
  localEvaluationEntity,
  onEvaluateGenericExpression,
  handleOpenModal,
  handleCloseModal,
  modalOpen,
  handleCopyToClipboard
}) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          value={localEvaluationEntity}
          valueKey='genericEvaluationEntityId'
          labelKey='name'
          label={t('GenericEvaluation.GenericEvaluationEntities')}
          options={genericEvaluationEntities}
          onChange={event => onGenericEvaluationParameterChanged('genericEvaluationEntity', event)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Tooltip title={t('GenericEvaluation.Tooltip.EntityIdentifier')}>
          <TextField
            name='Identifier'
            value={(evaluationParameters && evaluationParameters.identifier) || ''}
            label={t('GenericEvaluation.Identifier')}
            onChange={event => onGenericEvaluationParameterChanged('identifier', event)}
            fullWidth
          />
        </Tooltip>
      </Grid>
      <Grid item xs={12} sm={4} display='flex' gap={1} justifyContent='end'>
        <IconButton
          color='primary'
          tooltip={t('GenericEvaluation.EvaluateExpression')}
          onClick={onEvaluateGenericExpression}
          disabled={evaluatingExpression}
        >
          <MemoryIcon />
        </IconButton>
        <IconButton variant='outlined' color='primary' tooltip={t('GenericEvaluation.ViewDocStructure')} onClick={handleOpenModal}>
          <VisibilityIcon />
        </IconButton>
      </Grid>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        maxWidth='lg'
      >
        <DialogTitle id='alert-dialog-title'></DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <XMLViewer xml={evaluationResult?.xmlFile} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t('Close')}</Button>
          <CopyToClipboard id={`EvaluationLogs_Copy`} text={evaluationResult?.xmlFile}>
            <Button onClick={handleCopyToClipboard} id={`EvaluationLogs_Copy`}>
              {t('CopyToClipboard')}
            </Button>
          </CopyToClipboard>
        </DialogActions>
      </Dialog>
      <Grid item xs={12} sm={12}>
        <MultilineEditor
          formulaLabel={t('GenericEvaluation.Expression')}
          formulaPropertyName='expression'
          formulaPropertyValue={evaluationParameters.expression || ''}
          isReadOnly={false}
          onPropertyChanged={onGenericEvaluationParameterChanged}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          disabled
          name='result'
          value={(evaluationResult && evaluationResult.evaluationResult) || ''}
          label={t('GenericEvaluation.Result')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          disabled
          name='genericDocumentInfo'
          value={(evaluationResult && evaluationResult.genericDocumentInfo) || ''}
          label={t('GenericEvaluation.GenericDocumentInfo')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('GenericEvaluation.VariableName')}</TableCell>
              <TableCell>{t('GenericEvaluation.VariableValue')}</TableCell>
            </TableRow>
          </TableHead>
          {evaluationResult && evaluationResult.evaluationVariables && (
            <TableBody>
              {evaluationResult.evaluationVariables.map((item, idx) => {
                return (
                  <TableRow hover key={idx}>
                    <TableCell>{item.variableName}</TableCell>
                    <TableCell>{item.variableValue}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </Grid>
    </Grid>
  );
};

GenericEvaluationComponent.propTypes = {
  evaluatingExpression: PropTypes.bool.isRequired,
  evaluationParameters: PropTypes.object.isRequired,
  genericEvaluationEntities: PropTypes.array.isRequired,
  evaluationResult: PropTypes.object,
  onGenericEvaluationParameterChanged: PropTypes.func.isRequired,
  localEvaluationEntity: PropTypes.object.isRequired,
  onEvaluateGenericExpression: PropTypes.func.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  modalOpen: PropTypes.bool.isRequired,
  handleCopyToClipboard: PropTypes.func.isRequired
};

export default GenericEvaluationComponent;
